/**
 * Copyright (c) 2021, Trend Micro, Inc.
 * All rights reserved.
 */
import axios from 'axios';

const wsSessionApiEndpoint =
  DEPLOYMENT_TARGET === 'alpha' || DEPLOYMENT_TARGET === 'dev' ? `/trend-us-1` : '';

const API = axios.create({
  baseURL: wsSessionApiEndpoint,
  headers: {
    'api-version': 'v1',
    Accept: 'application/json',
  },
});

/**
 * Create WS Session API Call
 * @returns {Promise<{loggedInUser: *, status: number}>}
 * @param account
 * @param username
 * @param password
 * @param mfaCode
 */
export const createWSSession = async (account, username, password, mfaCode = '') => {
  const reqBody = {
    tenantName: account,
    userName: username,
    password: password,
    accessType: 'GUI',
  };
  if (mfaCode !== '') {
    reqBody.mfaCode = mfaCode;
  }
  try {
    const { data: loggedInUser, status } = await API.post(`/api/sessions`, reqBody);
    return { loggedInUser, status };
  } catch (error) {
    if (error.response && error.response.status !== 403) {
      return {
        error: error.response.data.message || error.message,
        status: error.response.status,
      };
    } else {
      // Something happened in authenticating WS user that triggered an Error
      console.error('Error Authenticating WS user: ', error.message);
    }
  }
};

/**
 * Create WS Session API Call
 * @returns {Promise<{resetUser: *, status: number}>}
 * @param account
 * @param username
 * @param recaptchaToken
 */
export const resetWSPassword = async (account, username, recaptchaToken = '') => {
  try {
    const { data: resetUser, status } = await API.post(`/api/passwordreset`, {
      account,
      username,
      recaptchaToken,
    });
    return { resetUser, status };
  } catch (error) {
    if (error.response && error.response.status !== 403) {
      return {
        error: error.response.data.message,
        status: error.response.status,
      };
    } else {
      console.error('Error resetting WS user password: ', error.message);
    }
  }
};

/**
 * Create WS Session API Call
 * @returns {Promise<{updatedUser: *, status: number}>}
 * @param newPassword
 * @param confirmNewPassword
 * @param resetCode
 */
export const updateWSPassword = async (newPassword, confirmNewPassword, resetCode) => {
  try {
    const { data: updatedUser, status } = await API.post(`/api/passwordupdate`, {
      newPassword,
      confirmNewPassword,
      resetCode,
    });
    return { updatedUser, status };
  } catch (error) {
    if (error.response && error.response.status !== 403) {
      return {
        error: error.response.data.message,
        status: error.response.status,
      };
    } else {
      console.error('Error updating WS user password: ', error.message);
    }
  }
};

/**
 * Delete Existing WS Based Session
 */
export const deleteWSSession = async () => {
  try {
    const { data: deletedUser, status } = await axios.delete(
      `${wsSessionApiEndpoint}/rest/session`,
      {
        headers: {
          'api-version': 'v1',
        },
      }
    );
    if (status === 204) {
      return { deletedUser, status };
    }
  } catch (error) {
    if (error.response && error.response.status !== 403) {
      return {
        error: error.response.data.message,
        status: error.response.status,
      };
    } else {
      console.error('Error deleting WS session: ', error.message);
    }
  }
};

/**
 * Confirm WS users GET request
 * @returns void
 * @param confirmationId
 */
export const confirmWSUser = async (confirmationId) => {
  if (!confirmationId) {
    return;
  }
  try {
    // using fetch to make GET call without headers
    await fetch(`/_workload_iframe/SignIn.screen?confirmation=${confirmationId}`);
    window.history.pushState({}, '', '/');
  } catch (error) {
    console.error('Error confirming WS user: ', error);
  }
};
